/**
 * Standalone async functions for world data operations.
 *
 * These functions call server actions and then update the Zustand store
 * with the results. Keeping them outside the store makes the store purely
 * synchronous and makes a future React Query migration trivial — just
 * wrap these functions in `useQuery` / `useMutation`.
 */

import {
    getCategoryEntriesTree,
    getConnectionsForWorld,
} from "@/lib/actions/wiki-editor-actions";
import { useWikiStore } from "@/stores/wiki-store";
import type { EntryNode, WorldConnection } from "@/stores/wiki-store";

export type WikiRequestIdentity = { wikiId: string | null; generation: number };

function currentWikiIdentity(): WikiRequestIdentity {
    const { wikiId, wikiGeneration: generation } = useWikiStore.getState();
    return { wikiId, generation };
}

function isCurrentWiki(expected: WikiRequestIdentity): boolean {
    const current = currentWikiIdentity();
    return Boolean(expected.wikiId) &&
        current.wikiId === expected.wikiId &&
        current.generation === expected.generation;
}

// ─── Initialize ──────────────────────────────────────────

/** Load connections for a world and set the wikiId in the store. */
export async function initializeWorld(
    wikiId: string,
    initialData?: {
        entriesByCategory: Record<string, EntryNode[]>;
        connections: WorldConnection[];
    }
): Promise<void> {
    const store = useWikiStore.getState();
    store.setWorldId(wikiId);
    const expectedWiki = currentWikiIdentity();

    if (initialData) {
        store.setConnections(initialData.connections);
        for (const [categoryId, entries] of Object.entries(initialData.entriesByCategory)) {
            store.setCategoryEntries(categoryId, entries);
        }
        return;
    }

    try {
        const conns = await getConnectionsForWorld(wikiId);
        if (isCurrentWiki(expectedWiki)) {
            useWikiStore.getState().setConnections(conns);
        }
    } catch (err) {
        console.error("Failed to load connections:", err);
    }
}

/** Force-refresh entries for a specific category from the server. */
export async function refreshCategory(
    categoryId: string,
    originatingWiki?: WikiRequestIdentity
): Promise<void> {
    const expectedWiki = originatingWiki ?? currentWikiIdentity();
    const entries = await getCategoryEntriesTree(categoryId);
    if (isCurrentWiki(expectedWiki)) {
        useWikiStore.getState().setCategoryEntries(categoryId, entries);
    }
}

/**
 * Reset cached entry data for the entire world and reload connections.
 * Callers should also trigger `router.refresh()` to re-fetch the categories
 * list from the server component that owns the page.
 */
export async function refreshWorld(): Promise<void> {
    const store = useWikiStore.getState();
    for (const categoryId of Object.keys(store.categoryEntries)) {
        store.removeCategoryState(categoryId);
    }
    await refreshConnections();
}

// ─── Connection Operations ───────────────────────────────

/** Reload all connections for the current world from the server. */
export async function refreshConnections(originatingWiki?: WikiRequestIdentity): Promise<void> {
    const expectedWiki = originatingWiki ?? currentWikiIdentity();
    const { wikiId } = expectedWiki;
    if (!wikiId) return;

    const conns = await getConnectionsForWorld(wikiId);
    if (isCurrentWiki(expectedWiki)) {
        useWikiStore.getState().setConnections(conns);
    }
}
